import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, MapPin } from 'lucide-react';
import { useAccessibleLocations } from '../hooks/useAccessibleLocations';
import { useIngredientCategories } from '../../inventory/hooks/useIngredientCategories';
import { reportsService } from '../services/reportsService';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import { Pagination } from '@/components/ui/pagination';

interface StockFilters {
  locationId: string;
  category: string;
}

interface StockByLocation {
  locationId: string;
  locationName: string;
  currentStock: number;
  minStock: number;
  maxStock: number;
  status: 'critical' | 'low' | 'normal' | 'excess';
  lastUpdated: string;
  totalValue: number;
}

interface StockItem {
  id: string;
  name: string;
  category: string;
  unit: string;
  unitCost: number;
  locations: StockByLocation[];
}

export const StockReport: React.FC = () => {
  const { categoryOptions } = useIngredientCategories();
  const { locationsWithAll, defaultLocationId } = useAccessibleLocations();

  const [filters, setFilters] = useState<StockFilters>({
    locationId: 'all',
    category: 'all',
  });
  const [stockData, setStockData] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-seleccionar local cuando se carga
  useEffect(() => {
    if (defaultLocationId && filters.locationId === 'all' && defaultLocationId !== 'all') {
      setFilters(prev => ({ ...prev, locationId: defaultLocationId }));
    }
  }, [defaultLocationId]);

  const categories = [
    { value: 'all', label: 'Todas las categorías' },
    ...categoryOptions,
  ];

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportsService.getStockReport(filters.locationId, {
        category: filters.category !== 'all' ? filters.category : undefined,
      });
      setStockData(data.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el reporte');
      console.error('Error loading stock report:', err);
    } finally {
      setLoading(false);
    }
  };

  // Obtener todas las sucursales únicas de los datos
  const allLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    stockData.forEach(item => {
      item.locations.forEach(loc => {
        if (!locMap.has(loc.locationId)) {
          locMap.set(loc.locationId, loc.locationName);
        }
      });
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [stockData]);

  // Configuración de tabla con sorting y paginación
  type SortField = 'name' | 'category' | 'unit';

  const fieldLabels: Record<SortField, string> = {
    name: 'Producto',
    category: 'Categoría',
    unit: 'Unidad',
  };

  const sortValueExtractor = (item: StockItem, field: SortField): any => {
    switch (field) {
      case 'name': return item.name.toLowerCase();
      case 'category': return item.category.toLowerCase();
      case 'unit': return item.unit.toLowerCase();
      default: return item.name.toLowerCase();
    }
  };

  const tableData = useTableData<StockItem, SortField>(
    {
      data: stockData,
      defaultSortField: 'name',
      defaultSortDirection: 'asc',
      itemsPerPage: 15,
      sortValueExtractor,
    },
    fieldLabels
  );

  const getStockTextColor = (status: string) => {
    switch (status) {
      case 'critical': return 'text-red-600 font-bold';
      case 'low': return 'text-yellow-600 font-semibold';
      case 'excess': return 'text-blue-600 font-semibold';
      default: return 'text-gray-900';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Reporte de Stock de Mercadería</h2>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Local</Label>
              <Select value={filters.locationId} onValueChange={(value) => setFilters(prev => ({ ...prev, locationId: value }))}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {locationsWithAll.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {location.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Categoría</Label>
              <Select value={filters.category} onValueChange={(value) => setFilters(prev => ({ ...prev, category: value }))}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleGenerateReport}
              disabled={loading}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {loading ? 'Cargando...' : 'Generar Reporte'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de stock */}
      {stockData.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <SortableHeader
                      field={'name' as SortField}
                      onSort={tableData.handleSort}
                      getSortIcon={tableData.getSortIcon}
                      className="text-left"
                    >
                      Producto
                    </SortableHeader>
                    <SortableHeader
                      field={'category' as SortField}
                      onSort={tableData.handleSort}
                      getSortIcon={tableData.getSortIcon}
                      className="text-left"
                    >
                      Categoría
                    </SortableHeader>
                    <SortableHeader
                      field={'unit' as SortField}
                      onSort={tableData.handleSort}
                      getSortIcon={tableData.getSortIcon}
                      className="text-left"
                    >
                      Unidad
                    </SortableHeader>
                    {allLocations.map(loc => (
                      <th
                        key={loc.id}
                        className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {loc.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tableData.pagination.paginatedData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <span className="text-sm font-medium text-gray-900">{item.name}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-500">{item.category}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-500">{item.unit}</span>
                      </td>
                      {allLocations.map(loc => {
                        const stockLoc = item.locations.find(l => l.locationId === loc.id);
                        if (!stockLoc) {
                          return (
                            <td key={loc.id} className="px-4 py-4 text-center text-gray-300">
                              —
                            </td>
                          );
                        }
                        return (
                          <td key={loc.id} className="px-4 py-4 text-center">
                            <span className={`text-sm ${getStockTextColor(stockLoc.status)}`}>
                              {stockLoc.currentStock}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-2 text-sm text-gray-500">
              Ordenado por: <span className="font-medium">{tableData.sortInfo.label}</span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
            </div>

            <Pagination
              currentPage={tableData.pagination.currentPage}
              totalPages={tableData.pagination.totalPages}
              onPageChange={tableData.pagination.goToPage}
              showingFrom={tableData.pagination.showingFrom}
              showingTo={tableData.pagination.showingTo}
              totalItems={tableData.pagination.totalItems}
              itemsPerPage={tableData.pagination.itemsPerPage}
              onItemsPerPageChange={tableData.pagination.setItemsPerPage}
            />
          </CardContent>
        </Card>
      )}

      {/* Estado vacío */}
      {!loading && stockData.length === 0 && (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Presiona "Generar Reporte" para ver el stock actual</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
